import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@mui/material/TextField'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import InputAdornment, { inputAdornmentClasses } from '@mui/material/InputAdornment'
import CancelIcon from '@mui/icons-material/Cancel'
import { KeyType, isKeyType, newEnterKeyDownEvent } from '@cbgms/base/utils/keyevent'
import { Box, SxProps, Theme } from '@mui/system'
import SearchDropdown from './components/SearchDropdown'
import { Paper, PaperProps, Popper, PopperProps } from '@mui/material'
import { SilentErrorIcon } from '../SilentErrorIcon'
import { Timing } from '@cbgms/base/utils/timing'
import { useDebouncedCallback } from 'use-debounce'
import { Loader } from '@carsys/ui/src/Loader'
import SearchIcon from '@mui/icons-material/Search'
import { ITopSearchResultProps } from './ResultsV2/ResultComponentSelectorV2'
import { useTopSearchContext } from '../context/TopSearchContext'
import { TopSearchGroupHeader } from '@cbgms/components/src/top-search/search-v2/TopSearchGroupHeader'
import { TopSearchResultItem } from '@cbgms/components/src/top-search/search-v2/TopSearchResultItem'
import { TopSearchResultList } from '@cbgms/components/src/top-search/search-v2/TopSearchResultList'
import { useFeature } from '@cbgms/base/feature-flags'

function PopperComponent(props: PopperProps) {
  return <Popper {...props} placement='bottom-start' />
}

type IPaperProps = PaperProps<React.ElementType, { loading: boolean }>

function PaperComponent({ children, loading, ...props }: IPaperProps) {
  return (
    <Paper {...props} sx={{ borderRadius: theme => `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px` }}>
      {children}
    </Paper>
  )
}

const Icon: React.FC<{ loading: boolean }> = ({ loading }) => {
  const [isLoading, setIsLoading] = useState(loading)
  const debouncedSetIsLoading = useDebouncedCallback(value => setIsLoading(value), Timing.ExtralongWait, { maxWait: Timing.ExtralongWait })

  useEffect(() => {
    if (loading) {
      debouncedSetIsLoading(true)
    } else {
      setIsLoading(false)
      debouncedSetIsLoading?.cancel()
    }
  }, [loading, setIsLoading, debouncedSetIsLoading])

  if (isLoading) {
    return (
      <Loader
        sx={{
          position: 'relative',
          paddingLeft: '24px',
          display: 'flex',
          alignContent: 'center',
          '&>*': {
            display: 'flex'
          }
        }}
        loading
        fading={false}
        size={24}
      />
    )
  }
  return <SearchIcon />
}

const optionLabelGetter = (option: string | ITopSearchResultProps | null) => {
  if (!option || typeof option === 'string') {
    return ''
  } else if (option.Vehicle) {
    return option.Vehicle.Values[0].Manufacturer
  } else if (option.WorkorderInvoice && option.WorkorderInvoice.InvoiceNumber) {
    return option.WorkorderInvoice.InvoiceNumber
  } else if (option.WorkorderQuotation && option.WorkorderQuotation.QuotationNumber) {
    return option.WorkorderQuotation.QuotationNumber
  } else if (option.VehicleSalesQuotation && option.VehicleSalesQuotation.QuotationNumber) {
    return option.VehicleSalesQuotation.QuotationNumber
  } else if (option.Order && option.Order.ID) {
    return option.Order.ID
  } else if (option.Account) {
    return `${option.Account.FirstName} ${option.Account.Name}`
  } else {
    return ''
  }
}

const textFieldStyleFn = (open: boolean, showSearchFilter: boolean) => {
  const textFieldStyle: SxProps = {
    // MuiAutocomplete-inputRoot
    [`& .${autocompleteClasses.inputRoot}`]: {
      transition: 'border-radius .5s', // takes a while to open the results, delay transition
      borderRadius: (theme: Theme) => {
        let brTL = theme.shape.borderRadius
        const brTR = theme.shape.borderRadius
        let brBL = theme.shape.borderRadius
        let brBR = theme.shape.borderRadius
        if (showSearchFilter) {
          // remove left-side radius when no category filter is show
          brTL = 0
          brBL = 0
        }
        if (open) {
          // remove bottom-side radius when 'open', a paper will render beneath the field.
          // to give the impressions that the fields belong together.
          brBL = 0
          brBR = 0
        }
        return `${brTL}px ${brTR}px ${brBR}px ${brBL}px`
      },
      py: 0,
      px: 1,
      backgroundColor: '#FFF !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: '#FFF !important'
      },
      '&::before, &::after': { content: 'none' }, // hide default 'focus' border
      [`& .${inputAdornmentClasses.root}, & .${inputAdornmentClasses.root}:not(.${inputAdornmentClasses.hiddenLabel})`]: {
        mt: '0 !important' // for some reason the start adorment has a margin top
      }
    }
  }

  return textFieldStyle
}

// create a backdrop to give focus to the search and search dropdown
const fakeBackDropStyleFn = (open: boolean) => {
  const fakeBackDropStyle: SxProps = {
    '&::before': {
      content: "''",
      transition: 'opacity ease-in-out 150ms',
      width: !open ? 0 : '100vw',
      height: !open ? 0 : '100vh',
      backgroundColor: 'rgba(0,0,0,0.35)',
      position: 'absolute',
      zIndex: -1,
      top: 0,
      left: 0,
      opacity: !open ? 0 : 1
    }
  }

  return fakeBackDropStyle
}

interface IBackDropProps {
  open: boolean
  onClick: () => void
}

export const BackDrop: React.FC<IBackDropProps> = ({ open, onClick }) => {
  React.useEffect(() => {
    const bodyElement = document.body
    if (bodyElement) {
      const hasNoVerticalScroll = bodyElement.scrollHeight <= bodyElement.clientHeight
      bodyElement?.classList?.toggle('no-bar', open && hasNoVerticalScroll)
      bodyElement?.classList?.toggle('stop-body-scroll', open)
    }
  }, [open])
  return (
    <Box data-testid='top-search-backdrop' sx={{ ...fakeBackDropStyleFn(open) }} className={open ? 'open' : 'closed'} onClick={onClick} />
  )
}

// useBufferedEnter will capture an 'Enter' keyboard event to handle it differently
// this is usefull when we have to wait for the api to return results while also allowing users press enter immediatly
// prevents the event from happening until the loaded bool turns true
// when other keyboard events happen in the mean time, it will swallow the event entirely
//
// returns
// - a ref to be passed to the input,
//  - onKeyDown function
const useBufferedEnter = (loading: boolean) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [shouldEnterWhenLoaded, setShouldEnterWhenLoaded] = useState(false)
  const [isSkippingBuffer, setIsSkippingBuffer] = useState(false)
  const bufferEnter = useCallback(
    (e: React.KeyboardEvent) => {
      // buffered enter: wait for load to complete and re-trigger enter after that
      e.stopPropagation()
      e.preventDefault()
      setShouldEnterWhenLoaded(true)
    },
    [setShouldEnterWhenLoaded]
  )
  const reset = useCallback(() => {
    // only call setState when its actually needed, otherwise it seems to lag the keyboard
    if (isSkippingBuffer) {
      setIsSkippingBuffer(false)
    }
    if (shouldEnterWhenLoaded) {
      setShouldEnterWhenLoaded(false)
    }
  }, [setShouldEnterWhenLoaded, setIsSkippingBuffer, shouldEnterWhenLoaded, isSkippingBuffer])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isKeyType(e, KeyType.ModifierKey)) {
        // ignore shift/contorl/alt keystrokes
        return
      }
      if (isKeyType(e, KeyType.EnterKey)) {
        if (loading && !isSkippingBuffer) {
          bufferEnter(e)
        } else {
          // non-buffered enter: allow event to bubble, reset state
          reset()
        }
        return
      }
      if (isKeyType(e, KeyType.ArrowDownKey) || isKeyType(e, KeyType.ArrowUpKey)) {
        // user selected a search suggestions. stop buffering and allow immediate selection
        setIsSkippingBuffer(true)
        return
      }
      // any other key was pressed:
      if (shouldEnterWhenLoaded || isSkippingBuffer) {
        // reset whenever a non-enter key is pressed
        reset()
      }
    },
    [setIsSkippingBuffer, isSkippingBuffer, shouldEnterWhenLoaded, reset, loading]
  )

  useEffect(() => {
    if (shouldEnterWhenLoaded && !loading && !!inputRef.current) {
      reset()
      // enter was pressed while loading, now we're done loading, trigger the enter
      inputRef.current.dispatchEvent(newEnterKeyDownEvent())
    }
  }, [loading, reset, shouldEnterWhenLoaded, inputRef])

  return useMemo(() => ({ inputRef, onKeyDown, reset }), [inputRef, onKeyDown, reset])
}

export interface TopSearchV2Props {
  iconOnly?: boolean
  setHasQuery?: (hasQuery: boolean) => void
  classes?: { icon?: string }
  isExpanded?: boolean | null
  popperWidth?: number | string
  disableMaxWidth?: boolean
  disableBackdrop?: boolean
}

export const TopSearchV2: React.FC<TopSearchV2Props> = ({
  iconOnly = false,
  classes = {},
  setHasQuery,
  isExpanded = false,
  popperWidth,
  disableMaxWidth = false,
  disableBackdrop = false
}) => {
  const { t } = useTranslation()
  const isTopbarUXChangeForTimerEnabled = useFeature('topbar-ux-change-for-timer')
  const {
    showSearchFilter,
    searchPlaceholder,
    noResultsPlaceholder,
    loading,
    results,
    clearQuery,
    setQuery,
    query,
    silentErrors,
    recentlyOpenedEntities,
    refetchRecentlyOpenedEntities,
    onClose,
    open,
    setOpen
  } = useTopSearchContext()
  const prevIsExpandedRef = useRef(Boolean(isExpanded))

  const maxWidth = React.useMemo(() => {
    if (disableMaxWidth) return 'none'
    return isTopbarUXChangeForTimerEnabled && showSearchFilter ? '600px' : '450px'
  }, [disableMaxWidth, isTopbarUXChangeForTimerEnabled, showSearchFilter])

  const onOpen = useCallback(() => {
    refetchRecentlyOpenedEntities()
    setOpen(true)
  }, [setOpen])

  useEffect(() => {
    if (setHasQuery) {
      setHasQuery(query.length > 0)
    }
  }, [query, setHasQuery])

  // determine if we should open the search based on business logic
  // collapse when loading
  // never open in productSearchingMode. Product search results are only shown in the product list. The search is executed on Enter
  const { searchOptions, controlledOpen } = React.useMemo(() => {
    if (loading && results.length === 0) return { searchOptions: [], controlledOpen: false }
    if (!query.length) return { searchOptions: recentlyOpenedEntities, controlledOpen: open && !!recentlyOpenedEntities?.length }
    return { searchOptions: results, controlledOpen: open }
  }, [loading, query, results, open])
  const { onKeyDown, inputRef, reset: resetBufferedEnter } = useBufferedEnter(loading)
  const handleOnchange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.preventDefault()
      setQuery(e.currentTarget?.value || '')
    },
    [setQuery]
  )

  const onSeachItemSelect = useCallback(
    (_: React.SyntheticEvent, option: string | ITopSearchResultProps | null) => {
      if (!option || typeof option === 'string') {
        return
      }
      option?.primaryAction?.()
      resetBufferedEnter()
    },
    [resetBufferedEnter]
  )

  useEffect(() => {
    const expandedNow = Boolean(isExpanded)
    const expandedFromUserInteraction = expandedNow && !prevIsExpandedRef.current

    if (expandedFromUserInteraction) {
      onOpen()
    }

    prevIsExpandedRef.current = expandedNow
  }, [isExpanded, onOpen])

  if (iconOnly) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
        <SearchIcon className={classes.icon} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexShrink: 1, flexBasis: 'auto', height: '100%', maxWidth, width: '100%' }}>
      {controlledOpen && <SilentErrorIcon silentErrors={silentErrors} />}
      {!disableBackdrop && <BackDrop open={open} onClick={onClose} />}
      {showSearchFilter && <SearchDropdown />}
      <Autocomplete
        clearOnEscape
        clearIcon={<CancelIcon data-testid='clear-search-button' />}
        id='search-autocomplete'
        data-testid='top-search-autocomplete'
        inputValue={query}
        sx={{ zIndex: 20 }}
        fullWidth
        PopperComponent={PopperComponent as any /* mui types inconsistant, fixed in mui-v6 */}
        PaperComponent={PaperComponent as React.ComponentType<React.PropsWithChildren<React.HTMLAttributes<HTMLElement>>>}
        componentsProps={{
          paper: { loading } as IPaperProps,
          ...(popperWidth != null ? { popper: { style: { width: popperWidth } } } : {})
        }}
        ListboxComponent={TopSearchResultList as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
        open={controlledOpen}
        ListboxProps={{
          style: {
            maxHeight: '90vh'
          }
        }}
        freeSolo
        selectOnFocus
        autoHighlight
        onInputChange={(_event, _value, reason) => {
          if (reason === 'clear') {
            clearQuery()
          }
        }}
        onChange={onSeachItemSelect}
        onOpen={onOpen}
        onClose={onClose}
        filterOptions={options => options}
        groupBy={option => (option.Title ? t(option.Title) : '')}
        renderGroup={params => <TopSearchGroupHeader {...params} />}
        getOptionLabel={optionLabelGetter}
        noOptionsText={t(noResultsPlaceholder)}
        options={searchOptions}
        renderOption={(props, option) => <TopSearchResultItem {...props} option={option} key={props.id} />}
        renderInput={params => (
          <TextField
            {...params}
            ref={inputRef}
            data-testid='top-menu-search-input'
            placeholder={t(searchPlaceholder)}
            onChange={handleOnchange}
            onKeyDown={onKeyDown}
            onBlur={onClose}
            variant='filled'
            sx={textFieldStyleFn(controlledOpen, showSearchFilter)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <Icon loading={loading} />
                </InputAdornment>
              )
            }}
          />
        )}
      />
    </Box>
  )
}
